import React from "react";

import BasePage from "./BasePage";
import type { Client } from "./App";
import { detectLang, enabledLangs, setDocumentLang, strings } from "./i18n";
import { initials } from "./mark";

/** THE CHOICE THE PRINTED QR CODE OPENS FIRST (TW-0206; picker.ts has the rule and Chris's words).
 *
 *  Linktree-shaped, as he put it: the shop's mark on top — its logo, or the same ink lettermark the form draws for a
 *  shop with none — its name, and one full-width button per thing the shop has turned on. Today that is two, and the
 *  waiver comes FIRST and is the filled one, because most people scanning a code on a counter came to sign: one tap
 *  and the form is right there, on this page, with no reload.
 *
 *  "Pay a deposit" is a plain link to the shop's deposit page (a full page load onto another host, which is what it
 *  is). Both buttons are 60px tall, black on white and white on black, so a thumb finds them and the contrast holds in
 *  a dim shop.
 *
 *  It speaks the language the form would open in (the phone's, or `?lang=`, and only one the shop offers), and it has
 *  no switch of its own: two buttons are not worth a control, and the form under the first carries the switch. */
export default function QrPicker({
  client,
  depositHref,
  onWaiver,
  onDeposit,
}: {
  client: Client;
  depositHref: string;
  onWaiver: () => void;
  onDeposit: () => void;
}) {
  const lang = detectLang(window.location.search, undefined, enabledLangs(client.public_data));
  const T = strings(lang);
  // the form sets the page's language for a screen reader when it opens; this screen stands where it would have
  React.useEffect(() => {
    setDocumentLang(lang);
  }, [lang]);

  return (
    <BasePage>
      <div data-testid="qr-picker" className="flex flex-col items-center py-10 text-center">
        {client.public_data?.logo_url ? (
          <img src={client.public_data.logo_url} alt="" className="mx-auto mb-3" style={{ width: 200 }} />
        ) : (
          <div
            aria-hidden
            className="mx-auto mb-4 flex items-center justify-center rounded-2xl bg-black font-bold text-white select-none"
            style={{ width: 88, height: 88, fontSize: 36, letterSpacing: 1 }}
          >
            {initials(client.name)}
          </div>
        )}
        <h1 className="px-4 text-3xl font-bold">{client.name}</h1>
        <div className="mt-8 flex w-full max-w-sm flex-col gap-y-3 px-2">
          <button
            type="button"
            data-testid="qr-picker-waiver"
            onClick={onWaiver}
            className="w-full rounded-lg border-2 border-black bg-black px-6 py-4 text-lg font-semibold text-white"
          >
            {T.pickerWaiver}
          </button>
          <a
            href={depositHref}
            data-testid="qr-picker-deposit"
            onClick={onDeposit}
            className="block w-full rounded-lg border-2 border-black bg-white px-6 py-4 text-lg font-semibold text-black"
          >
            {T.pickerDeposit}
          </a>
        </div>
      </div>
    </BasePage>
  );
}
